import { MARGIN, PAGE_HEIGHT, USABLE_PAGE_HEIGHT } from './geometry';

/**
 * Zwei-Phasen-Layout-Engine: MESSEN → PLATZIEREN. Zeichnen bleibt beim Aufrufer.
 *
 * Aus `@kit/self-disclosure` herausgelöst (Spec `2026-07-30-legal-pdf-renderer.md`, PR-2),
 * Logik unverändert. Abgesichert durch den Golden-Master in
 * `self-disclosure/__tests__/render-golden.test.ts` — eine Verschiebung um 1 pt lässt dort
 * 24 Snapshots fallen.
 *
 * Jede zeichenbare Einheit ist ein `LayoutBlock` aus primitiven Rows. Dieselbe Row-Liste
 * treibt Messung (Summe der Höhen) und Zeichnung — eine Messfunktion für beides, damit der
 * Test-Harness nicht vom echten Renderer abweichen kann.
 *
 * Platzierungsregeln (rein, deterministisch):
 *  - keep-together: Ein Block, der nicht mehr auf die Seite passt, bricht die Seite VOR sich um.
 *  - repeatHeader: Table-Blöcke tragen ihre Kopfzeile mit; wird vor einem solchen Block
 *    umbrochen, wird die Kopfzeile oben auf der neuen Seite zuerst wiederholt. Seiten-bewusst,
 *    nicht bloß umbruch-getrieben: Eine Seite, die von einem Nicht-Table-Block eröffnet wurde,
 *    bekommt die Kopfzeile trotzdem vor ihrer ersten Table-Row.
 *  - Orphan-Regel: `splittable`-Blöcke dürfen nur an Row-Grenzen geteilt werden, und nur wenn
 *    ≥ 3 Rows auf der aktuellen Seite bleiben.
 *  - Degenerate-Guard: Ein Block, der höher als eine nutzbare Seite ist, fällt auf zeilenweisen
 *    Fluss zurück — kontrolliert, ohne Endlosschleife, mit `console.warn`.
 *
 * GENERIK (Review-Finding, PR-2): Die Engine liest von einer Row ausschließlich `height`.
 * Deshalb ist `TRow` ein Typparameter mit `LayoutRowBase`-Constraint — jeder Consumer bringt
 * seine eigene Row-Union und seine eigene Zeichenfunktion mit, die Platzierungslogik existiert
 * genau einmal.
 */

/** Orphan-Regel: Absatz nur teilen, wenn ≥ 3 Zeilen zurückbleiben. */
const ORPHAN_MIN_ROWS = 3;

/** Das Einzige, was die Engine von einer Row wissen muss. */
export interface LayoutRowBase {
  height: number;
}

export interface LayoutBlock<TRow extends LayoutRowBase> {
  id: string;
  rows: TRow[];
  /** Absatz-Blöcke dürfen unter der Orphan-Regel an Row-Grenzen geteilt werden. */
  splittable?: boolean;
  /** Erzwingt eine frische Seite vor dem Block. */
  pageBreakBefore?: boolean;
  /**
   * Kopfzeilen-Wiederholung: Vor dem ERSTEN Block mit dieser Kopfzeile auf einer Seite
   * werden deren Rows einmal platziert — die Kopfzeile erscheint also am Tabellenanfang
   * und auf jeder Folgeseite, auf der die Tabelle weiterläuft.
   */
  repeatHeader?: { id: string; rows: TRow[] };
}

/** Zwischenraum zwischen Blöcken; wird an Seitenenden stillschweigend verbraucht. */
export interface LayoutSpacer {
  spacer: number;
}

export type LayoutItem<TRow extends LayoutRowBase> =
  | LayoutBlock<TRow>
  | LayoutSpacer;

export interface PlacedBlockFragment<TRow extends LayoutRowBase> {
  blockId: string;
  /** 1-basierter Seitenindex. */
  page: number;
  /** Oberkante des Fragmentbands (PDF-Koordinaten, y wächst nach oben). */
  topY: number;
  height: number;
  rows: TRow[];
}

export interface PlacedLayout<TRow extends LayoutRowBase> {
  fragments: PlacedBlockFragment<TRow>[];
  pageCount: number;
  /** Blöcke, die eine Seite überschritten haben und zeilenweise geflossen sind. */
  degenerateBlockIds: string[];
}

/**
 * Zusätzlicher Rand für Seitenkopf und -fuß (PR-3).
 *
 * Defaults 0 — damit bleibt die Platzierung des Selbstauskunft-Renderers bit-genau
 * unverändert, was der Golden-Master absichert. ACHTUNG (Review-Finding F12): Die Insets
 * müssen an ALLEN VIER Stellen wirken, an denen die Engine gegen den Satzspiegel rechnet.
 * Der Golden-Test läuft mit Insets 0 und kann eine vergessene Stelle NIE sehen — die
 * Inset-Unit-Tests sind die einzige Absicherung dagegen.
 */
export interface LayoutMetrics {
  /** Platz oben, z. B. für Running-Header. */
  topInset?: number;
  /** Platz unten, z. B. für "Seite X von Y". */
  bottomInset?: number;
}

export interface PlaceLayoutOptions extends LayoutMetrics {
  /**
   * Label für die Degenerate-Warnung. Ohne Angabe generisch — der Selbstauskunft-Renderer
   * übergibt `self-disclosure`, damit die bisherige Logzeile unverändert bleibt.
   */
  label?: string;
}

function rowsHeight<TRow extends LayoutRowBase>(rows: readonly TRow[]): number {
  return rows.reduce((sum, row) => sum + row.height, 0);
}

/**
 * Platzierungsphase: misst jeden Block und weist Seite + topY zu, ohne zu zeichnen.
 * Rein und deterministisch — von den Layout-Tests direkt genutzt (inkl. synthetischer
 * Degenerate-Blöcke).
 */
export function placeLayoutItems<TRow extends LayoutRowBase>(
  items: readonly LayoutItem<TRow>[],
  options: PlaceLayoutOptions = {},
): PlacedLayout<TRow> {
  const label = options.label ?? 'pdf-layout';
  const topInset = options.topInset ?? 0;
  const bottomInset = options.bottomInset ?? 0;

  // Die vier Stellen, an denen gegen den Satzspiegel gerechnet wird (F12):
  // (1) Seitenoberkante, (2) nutzbare Höhe, (3) Boden im Degenerate-Fluss,
  // (4) verbleibender Platz. Alle vier MÜSSEN die Insets kennen.
  const pageFloor = MARGIN + bottomInset; // (3)
  const usableHeight = USABLE_PAGE_HEIGHT - topInset - bottomInset; // (2)

  const fragments: PlacedBlockFragment<TRow>[] = [];
  const degenerateBlockIds: string[] = [];
  const pageTop = PAGE_HEIGHT - MARGIN - topInset; // (1)

  let page = 1;
  let cursorY = pageTop;

  /** Letzte Seite, auf der eine gegebene repeatHeader-ID platziert wurde. */
  const headerPlacedOnPage = new Map<string, number>();

  const newPage = () => {
    page += 1;
    cursorY = pageTop;
  };

  for (const item of items) {
    if ('spacer' in item) {
      // moveDown-Semantik: Lücken verbrauchen nur Platz; eine Lücke, die über den Rand
      // hinausläuft, lässt den NÄCHSTEN Block umbrechen (nie eine Lücke allein).
      cursorY -= item.spacer;
      continue;
    }

    if (item.pageBreakBefore && cursorY < pageTop) {
      newPage();
    }

    const headerRows = item.repeatHeader?.rows ?? null;
    const headerHeight = headerRows ? rowsHeight(headerRows) : 0;

    let rows = item.rows;
    let height = rowsHeight(rows);

    if (height > usableHeight - headerHeight) {
      // Degenerate-Guard: Der Block passt auf keine Seite — Rückfall auf zeilenweisen Fluss,
      // damit er intern umbrechen darf. Fortschritt garantiert: Eine Row wird nur dann auf
      // eine neue Seite verschoben, wenn die aktuelle Seite nicht frisch ist.
      degenerateBlockIds.push(item.id);
      console.warn(
        `[${label}] layout block "${item.id}" measures ${Math.round(height)}pt (> one page) – falling back to row-wise flow`,
      );

      let open: TRow[] = [];
      let openTop = cursorY;

      const flush = () => {
        if (open.length > 0) {
          fragments.push({
            blockId: item.id,
            page,
            topY: openTop,
            height: rowsHeight(open),
            rows: open,
          });
          open = [];
        }
      };

      for (const row of rows) {
        if (cursorY - row.height < pageFloor && cursorY < pageTop) {
          flush();
          newPage();
          openTop = cursorY;
        }

        open.push(row);
        cursorY -= row.height;
      }

      flush();
      continue;
    }

    for (;;) {
      const remaining = cursorY - pageFloor; // (4)
      const needsHeader =
        headerRows !== null &&
        item.repeatHeader !== undefined &&
        headerPlacedOnPage.get(item.repeatHeader.id) !== page;
      const needed = height + (needsHeader ? headerHeight : 0);

      if (needed <= remaining) {
        if (needsHeader && headerRows && item.repeatHeader) {
          fragments.push({
            blockId: item.repeatHeader.id,
            page,
            topY: cursorY,
            height: headerHeight,
            rows: headerRows,
          });
          headerPlacedOnPage.set(item.repeatHeader.id, page);
          cursorY -= headerHeight;
        }

        fragments.push({ blockId: item.id, page, topY: cursorY, height, rows });
        cursorY -= height;
        break;
      }

      if (item.splittable) {
        // Orphan-Regel: nur teilen, wenn ≥ ORPHAN_MIN_ROWS Rows zurückbleiben;
        // sonst wandert der ganze Absatz auf die nächste Seite.
        let fit = 0;
        let used = 0;

        for (const row of rows) {
          if (used + row.height > remaining) break;
          used += row.height;
          fit += 1;
        }

        if (fit >= ORPHAN_MIN_ROWS) {
          fragments.push({
            blockId: item.id,
            page,
            topY: cursorY,
            height: used,
            rows: rows.slice(0, fit),
          });
          rows = rows.slice(fit);
          height = rowsHeight(rows);
          newPage();
          continue;
        }
      }

      // keep-together: Seitenumbruch VOR dem Block. Der Degenerate-Guard oben stellt sicher,
      // dass Header + Block auf eine frische Seite passen → die nächste Iteration platziert ihn.
      newPage();
    }
  }

  return { fragments, pageCount: page, degenerateBlockIds };
}
